import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { useGoals } from '../contexts/GoalsContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../../lib/supabaseClient';
import { Flame, Dumbbell, Utensils, Gauge, Calendar, User, Trophy, Droplets, Moon } from 'lucide-react';

interface DailyProgress {
  water_ml: number;
  workout_completed: boolean;
  sleep_hours: number | null;
  meals_logged: number;
  date: string;
}

interface UserData {
  name: string;
}

interface DashboardProps {
  onNavigate: (page: string) => void;
}

// Frases motivacionais (apenas para a frase final)
const finalPhrases = [
  "O trabalho devolve."
];

export default function Dashboard({ onNavigate }: DashboardProps) {
  const { user } = useAuth();
  const { profileAvatar } = useProfile();
  const { goalsUpdated } = useGoals();
  const { xp, totalScore, offensiveScore } = useGamification();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [last7Days, setLast7Days] = useState<DailyProgress[]>([]);
  const [streakDays, setStreakDays] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [memberNumber, setMemberNumber] = useState<number | null>(null);
  
  // Estados para animação de feedback
  const [prevXp, setPrevXp] = useState(0);
  const [prevOffensiveScore, setPrevOffensiveScore] = useState(0);
  const [xpAnimation, setXpAnimation] = useState<{ value: number; key: number } | null>(null);
  const [offensiveAnimation, setOffensiveAnimation] = useState<{ value: number; key: number } | null>(null);

  // Função para navegação
  const navigate = (path: string) => {
    const page = path.replace(/^\//, '');
    const pageMap: Record<string, string> = {
      'workouts': 'workouts',
      'nutrition': 'nutrition',
      'profile': 'profile',
      'login': 'signin'
    };

    if (pageMap[page]) {
      onNavigate(pageMap[page]);
    }
  };

  // Função para navegar para nutrição com lógica de dieta selecionada
  const handleNutritionNavigation = () => {
    const selectedPlan = localStorage.getItem('shapex_selected_plan');
    
    if (!selectedPlan) {
      // Primeira vez - não há dieta selecionada, redirecionar para seleção
      navigate('nutrition');
    } else {
      // Dieta já selecionada - redirecionar direto para acompanhamento nutricional
      localStorage.setItem('openCurrentMeal', 'true');
      navigate('nutrition');
    }
  };

  // Paleta visual
  const BG_DARK = '#0D0D0D';

  const [currentFinalPhrase, setCurrentFinalPhrase] = useState(finalPhrases[0]);

  useEffect(() => {
    const today = new Date().getDate();
    setCurrentFinalPhrase(finalPhrases[today % finalPhrases.length]);
  }, []);

  // Buscar número de membro do banco
  const calculateMemberNumber = useCallback(async (userId: string) => {
    try {
      // Buscar member_number diretamente do banco
      const { data, error } = await supabase
        .from('profiles')
        .select('member_number')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Erro ao buscar número de membro:', error);
        setMemberNumber(1);
        return;
      }

      if (data?.member_number) {
        // Se já tem member_number, usar ele
        setMemberNumber(data.member_number);
      } else {
        // Se não tem, o trigger deveria ter atribuído, mas vamos calcular como fallback
        // Buscar maior member_number e adicionar 1
        const { data: maxData } = await supabase
          .from('profiles')
          .select('member_number')
          .order('member_number', { ascending: false })
          .limit(1)
          .maybeSingle();

        const newMemberNumber = (maxData?.member_number || 0) + 1;
        
        // Salvar no banco
        await supabase
          .from('profiles')
          .update({ member_number: newMemberNumber })
          .eq('id', userId);
        
        setMemberNumber(newMemberNumber);
      }
    } catch (error) {
      console.error('Erro ao calcular número de membro:', error);
      setMemberNumber(1);
    }
  }, []);

  const loadDashboardData = useCallback(async () => {
    if (!user) return;

    try {
      // Calcular número de membro
      await calculateMemberNumber(user.id);

      // Carregar dados do perfil do usuário
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('id', user.id)
        .single();

      if (profileData && profileData.full_name) {
        setUserData({ name: profileData.full_name });
        setAvatarUrl(profileAvatar || profileData.avatar_url);
      } else {
        const { data: userData } = await supabase
          .from('user_onboarding')
          .select('name')
          .eq('user_id', user.id)
          .single();

        if (userData) {
          setUserData(userData);
        } else {
          const { data: fallbackData } = await supabase
            .from('users')
            .select('name')
            .eq('id', user.id)
            .single();

          if (fallbackData) {
            setUserData(fallbackData);
          }
        }
      }

      // Carregar últimos 7 dias
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 6);

      const { data: progressData } = await supabase
        .from('daily_progress')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate.toISOString().split('T')[0])
        .lte('date', endDate.toISOString().split('T')[0])
        .order('date', { ascending: true });

      setLast7Days(progressData || []);

      const calculateStreak = (data: DailyProgress[]) => {
        let streak = 0;
        const sortedData = [...data].reverse();

        for (const day of sortedData) {
          const dayScore = calculateDayScore(day);
          if (dayScore >= 70) {
            streak++;
          } else {
            break;
          }
        }

        setStreakDays(streak);
      };

      calculateStreak(progressData || []);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [user, profileAvatar, calculateMemberNumber]);

  useEffect(() => {
    const handleProfileUpdate = () => {
      loadDashboardData();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, [loadDashboardData]);

  // Escutar atualizações de progresso diário para sincronização em tempo real
  useEffect(() => {
    const handleDailyProgressUpdate = () => {
      loadDashboardData();
    };

    window.addEventListener('dailyProgressUpdated', handleDailyProgressUpdate);
    return () => window.removeEventListener('dailyProgressUpdated', handleDailyProgressUpdate);
  }, [loadDashboardData]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user, loadDashboardData, goalsUpdated]);

  // Detectar mudanças e mostrar animações de feedback
  useEffect(() => {
    if (xp > prevXp) {
      const diff = xp - prevXp;
      setXpAnimation({ value: diff, key: Date.now() });
      setTimeout(() => setXpAnimation(null), 1500);
    }
    setPrevXp(xp);
  }, [xp, prevXp]);

  useEffect(() => {
    if (offensiveScore > prevOffensiveScore) {
      const diff = Math.floor(offensiveScore - prevOffensiveScore);
      if (diff > 0) {
        setOffensiveAnimation({ value: diff, key: Date.now() });
        setTimeout(() => setOffensiveAnimation(null), 1500);
      }
    }
    setPrevOffensiveScore(offensiveScore);
  }, [offensiveScore, prevOffensiveScore]);

  const calculateDayScore = (day: DailyProgress) => {
    let score = 0;
    const waterScore = Math.min((day.water_ml / 2500) * 25, 25);
    score += waterScore;
    score += day.workout_completed ? 25 : 0;
    const mealScore = Math.min((day.meals_logged / 6) * 25, 25);
    score += mealScore;
    if (day.sleep_hours && day.sleep_hours >= 7 && day.sleep_hours <= 9) {
      score += 25;
    } else if (day.sleep_hours && day.sleep_hours >= 6) {
      score += 15;
    } else if (day.sleep_hours && day.sleep_hours >= 5) {
      score += 10;
    }
    return score;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BG_DARK }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E53935]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
      {/* Header */}
      <header className="pt-12 pb-6 px-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white mb-2">
              Bem-vindo, {userData?.name?.split(' ')[0] || 'Atleta'}
            </h1>
            <p className="text-white text-sm mb-1 flex items-center gap-2">
              Você está no modo caverna
              <Droplets className="w-4 h-4 text-[#E53935]" />
            </p>
            {memberNumber !== null && (
              <p className="text-[#A1A1A1] text-xs">
                Membro nº {memberNumber.toString().padStart(2, '0')}
              </p>
            )}
          </div>
          <button onClick={() => navigate('profile')} className="relative group">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#E53935] group-hover:border-[#E53935] transition-colors">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Perfil" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#E53935]/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#E53935]" />
                </div>
              )}
            </div>
          </button>
        </div>
      </header>

      <div className="px-6 space-y-6">
        {/* 1️⃣ Pontuação Ofensiva - SEMPRE PRIMEIRO */}
        <div className="p-5 rounded-[18px] bg-[#141414] border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg bg-[#E53935]/20">
              <Gauge className="w-5 h-5 text-[#E53935]" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm mb-2">Pontuação Ofensiva</h3>
              <p className="text-[#A1A1A1] text-xs opacity-70">Consistência semanal</p>
            </div>
          </div>
          <p className="text-[#A1A1A1] text-xs mb-5 leading-relaxed opacity-70">
            Aqui você não enrola. Você executa. Cada ação disciplinada soma pontos e mostra que você ainda está lutando.
          </p>
          <div className="flex items-baseline gap-2 mb-4 relative">
            <span className="text-4xl font-bold text-white relative inline-block">{offensiveScore}</span>
            <span className="text-sm text-[#A1A1A1]">/100</span>
            {offensiveAnimation && (
              <span
                key={offensiveAnimation.key}
                className="absolute left-0 top-[-10px] text-[#E53935] font-bold text-lg pointer-events-none"
                style={{
                  animation: 'fadeUp 1.5s ease-out',
                }}
              >
                +{offensiveAnimation.value}
              </span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
              <div className="text-white font-bold text-xs mb-1">{offensiveScore}</div>
              <div className="text-[#A1A1A1] text-[10px]">OFENSIVA</div>
            </div>
            <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
              <div className="text-white font-bold text-xs mb-1">
                {last7Days.filter(d => d.workout_completed).length}
              </div>
              <div className="text-[#A1A1A1] text-[10px]">TREINOS</div>
            </div>
            <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
              <div className="text-white font-bold text-xs mb-1">
                {last7Days.reduce((sum, d) => sum + d.meals_logged, 0)}
              </div>
              <div className="text-[#A1A1A1] text-[10px] text-center flex items-center justify-center">REFEIÇÕES</div>
            </div>
            <div className="bg-[#1A1A1A] rounded-lg p-3 text-center">
              <div className="text-white font-bold text-xs mb-1">
                {last7Days.reduce((sum, d) => sum + (d.sleep_hours || 0), 0).toFixed(0)}h
              </div>
              <div className="text-[#A1A1A1] text-[10px]">SONO</div>
            </div>
          </div>
        </div>

        {/* 2️⃣ Cards de ações - Próximo Treino / Próxima Refeição */}
        <div className="grid grid-cols-2 gap-4">
          {/* Próximo Treino */}
          <button
            onClick={() => navigate('workouts')}
            className="p-4 rounded-[18px] bg-[#141414] border border-white/10 relative overflow-hidden group text-left"
          >
            <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity">
              <Dumbbell className="w-16 h-16 text-white" />
            </div>
            <div className="relative z-10">
              <div className="p-2 rounded-lg bg-[#E53935]/20 w-fit mb-3">
                <Dumbbell className="w-5 h-5 text-[#E53935]" />
              </div>
              <h3 className="text-white font-bold text-sm mb-1">Próximo Treino</h3>
              <p className="text-[#A1A1A1] text-xs">Entrar no treino</p>
            </div>
          </button>

          {/* Próxima Refeição */}
          <button
            onClick={handleNutritionNavigation}
            className="p-4 rounded-[18px] bg-[#141414] border border-white/10 relative overflow-hidden group text-left"
          >
            <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity">
              <Utensils className="w-16 h-16 text-white" />
            </div>
            <div className="relative z-10">
              <div className="p-2 rounded-lg bg-[#E53935]/20 w-fit mb-3">
                <Utensils className="w-5 h-5 text-[#E53935]" />
              </div>
              <h3 className="text-white font-bold text-sm mb-1">Próxima Refeição</h3>
              <p className="text-[#A1A1A1] text-xs">Ver refeição</p>
            </div>
          </button>
        </div>

        {/* 3️⃣ Métricas acumuladas - Ofensivas Seguidas e Pontuação Total */}
        <div className="grid grid-cols-2 gap-4">
          {/* Ofensivas seguidas */}
          <div className="p-4 rounded-[18px] bg-[#141414] border border-white/10 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-5">
              <Flame className="w-16 h-16 text-white" />
            </div>
            <div className="relative z-10">
              <div className="p-2 rounded-lg bg-[#E53935]/20 w-fit mb-3">
                <Flame className="w-5 h-5 text-[#E53935]" />
              </div>
              <h3 className="text-white font-bold text-sm mb-2">Ofensivas seguidas</h3>
              <p className="text-[#A1A1A1] text-xs mb-4 opacity-70 leading-relaxed">
                Sua sequência de disciplina. O jogo é simples: não quebrar a corrente.
              </p>
              <div className="text-3xl font-bold text-white relative">
                {streakDays}
              </div>
            </div>
          </div>

          {/* Pontuação Total */}
          <div className="p-4 rounded-[18px] bg-[#141414] border border-white/10 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-5">
              <Trophy className="w-16 h-16 text-white" />
            </div>
            <div className="relative z-10">
              <div className="p-2 rounded-lg bg-[#E53935]/20 w-fit mb-3">
                <Trophy className="w-5 h-5 text-[#E53935]" />
              </div>
              <h3 className="text-white font-bold text-sm mb-2">Pontuação Total</h3>
              <p className="text-[#A1A1A1] text-xs mb-4 opacity-70 leading-relaxed">
                Sua evolução geral. Resultado direto do que você faz diariamente, não do que você promete.
              </p>
              <div className="text-3xl font-bold text-white relative inline-block">
                {totalScore}
                {xpAnimation && (
                  <span
                    key={xpAnimation.key}
                    className="absolute left-0 top-[-10px] text-[#E53935] font-bold text-lg pointer-events-none"
                    style={{
                      animation: 'fadeUp 1.5s ease-out',
                    }}
                  >
                    +{xpAnimation.value}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 4️⃣ Sua Evolução */}
        <div className="p-5 rounded-[18px] bg-[#141414] border border-white/10">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-[#E53935]/20">
                <Calendar className="w-4 h-4 text-[#E53935]" />
              </div>
              <h3 className="text-white font-bold text-sm">Sua evolução</h3>
            </div>
            <span className="text-[#A1A1A1] text-xs">Últimos 7 dias</span>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 sm:gap-3 overflow-x-auto">
            {last7Days.map((day, index) => {
              const date = new Date(day.date);
              const weekDay = date.toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase().slice(0, 3);
              const isCompleted = calculateDayScore(day) >= 70;

              return (
                <div key={index} className="bg-[#1A1A1A] rounded-lg p-2 sm:p-3 text-center min-w-0 flex-shrink-0">
                  <div className="flex items-center justify-center gap-1.5 mb-2 sm:mb-3">
                    <span className="text-white text-[10px] font-bold truncate">{weekDay}</span>
                    {isCompleted ? (
                      <div className="w-2 h-2 rounded-full bg-[#E53935] flex-shrink-0"></div>
                    ) : (
                      <div className="w-2 h-2 rounded-full border border-white/20 flex-shrink-0"></div>
                    )}
                  </div>
                  <div className="text-[#A1A1A1] text-[10px] mb-1 sm:mb-2">Água</div>
                  <div className="text-white text-[10px] font-bold mb-2 sm:mb-3 truncate">{(day.water_ml / 1000).toFixed(1)}L</div>
                  <div className="flex items-center gap-1 sm:gap-1.5 justify-center mb-1 sm:mb-2">
                    <div className="bg-[#141414] rounded p-1 flex-shrink-0">
                      <Utensils className="w-3 h-3 text-[#E53935]" />
                    </div>
                    <span className="text-white text-[10px] truncate min-w-0">{day.meals_logged}</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5 justify-center">
                    <div className="bg-[#141414] rounded p-1 flex-shrink-0">
                      <Moon className="w-3 h-3 text-purple-400" />
                    </div>
                    <span className="text-white text-[10px] truncate min-w-0">
                      {day.sleep_hours ? `${day.sleep_hours.toFixed(1)}h` : '-'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Frase Final */}
        <div className="py-8 text-center">
          <p className="text-[#525252] text-xs uppercase tracking-[0.2em] font-medium">
            {currentFinalPhrase}
          </p>
        </div>
      </div>
    </div>
  );
}
